import { Component, ErrorHandler, Inject, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { FieldTree, form, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { TagiVaritService } from 'app/_angular/service/tagi-varit.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { AsiakasTagi, TaginVarit } from 'app/_jaettu-lemonator/model/asiakas'
import { YllapitoUriService } from 'app/_jaettu-lemonator/service/yllapito-uri.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'

export interface TagiDialogData {
  tagi: AsiakasTagi | null
  kaikkiTagit: AsiakasTagi[]
}

interface TagiLomake {
  nimi: string
  poistettu: boolean
}

@Component({
  templateUrl: './tagi.dialog.html',
  styleUrls: ['./tagi.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TagiDialog implements OnInit {
  lomake: FieldTree<TagiLomake>
  onkoUusiTagi: boolean

  varit: TaginVarit[]
  valittuVari: TaginVarit

  private _buttonPushInflight: boolean = false
  private readonly _injector = inject(Injector)

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: TagiDialogData,
    private _dialogRef: MatDialogRef<TagiDialog>,
    private _errorHandler: ErrorHandler,
    private _firebaseLemonator: FirebaseLemonator,
    private _yllapitoUriService: YllapitoUriService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _timestampService: TimestampService,
    private _ladataanService: LadataanService,
    private _tagiVaritService: TagiVaritService,
    private _snackbar: MatSnackBar,
  ) {}

  ngOnInit() {
    this.onkoUusiTagi = !this._data.tagi
    this.varit = this._tagiVaritService.kaikkiVarit
    this.valittuVari = this._data.tagi ? this._data.tagi.varit : this._tagiVaritService.annaVahitenKaytettyVari(this._data.kaikkiTagit.map(tagi => tagi.varit.varikoodi))
    this.lomake = form(
      signal<TagiLomake>({ nimi: this._data.tagi?.nimi ?? '', poistettu: this._data.tagi?.poistettu ?? false }),
      polku => {
        required(polku.nimi)
      },
      { injector: this._injector },
    )
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  valitseVari(vari: TaginVarit) {
    this.valittuVari = vari
  }

  async tallenna() {
    if (!this.lomake().valid()) {
      this.lomake().markAsTouched()
      return
    }

    if (this._buttonPushInflight) {
      return
    }
    this._buttonPushInflight = true

    this._ladataanService.aloitaLataaminen()

    try {
      const kirjanpitaja = await this._kirjautunutKayttajaService.getKirjanpitajanTiedot()
      const kirjanpitajaUid = kirjanpitaja.uid

      const nimi = this.lomake.nimi().value()
      const poistettu = this.lomake.poistettu().value()
      const now = this._timestampService.now()

      let tagi: AsiakasTagi
      if (this._data.tagi) {
        tagi = {
          ...this._data.tagi,
          nimi: nimi.trim(),
          varit: this.valittuVari,
          poistettu: poistettu,
          paivittaja: kirjanpitajaUid,
          paivitetty: now,
        }
      } else {
        tagi = {
          avain: this._firebaseLemonator.firestoreCreateId(),
          nimi: nimi.trim(),
          varit: this.valittuVari,
          poistettu: poistettu,
          luoja: kirjanpitajaUid,
          luotu: now,
          paivittaja: kirjanpitajaUid,
          paivitetty: now,
        }
      }

      await this._firebaseLemonator.firestoreSetData(this._yllapitoUriService.annaAsiakasTagiUri(tagi.avain), tagi)
      this._dialogRef.close()
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Tagin tallennus epäonnistui', 'OK', { duration: 5000 })
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._buttonPushInflight = false
    }
  }
}
